export const PUBLIC_AUTH_ERROR_CODES = [
  "no_tenant",
  "account_suspended",
  "account_removed",
  "no_access_for_workspace",
  "unknown_workspace_domain",
  "no_workspace_context",
  "auth_callback_failed",
] as const;

export type PublicAuthErrorCode = (typeof PUBLIC_AUTH_ERROR_CODES)[number];

export const AUTH_ERROR_MESSAGES: Record<PublicAuthErrorCode, string> = {
  no_tenant:
    "Your account isn’t associated with a workspace yet. Contact your admin to get access.",
  account_suspended:
    "Your account is suspended and cannot access this workspace. Contact your admin if you need access restored.",
  account_removed:
    "Your account was removed from this workspace. Contact your admin if you need access again.",
  no_access_for_workspace:
    "Your account does not have access to this workspace. Contact your admin if you think this is a mistake.",
  unknown_workspace_domain:
    "This workspace domain is not configured. Contact support if you expected access here.",
  no_workspace_context:
    "We couldn’t determine your active workspace. Please sign in again from your workspace domain.",
  auth_callback_failed:
    "Sign-in failed. Please try again or contact support if the problem persists.",
};

export const AUTH_ERRORS_THAT_CLEAR_LOCAL_SESSION = new Set<PublicAuthErrorCode>([
  "account_suspended",
  "account_removed",
  "no_access_for_workspace",
  "no_workspace_context",
]);

export const SESSION_CONTEXT_FAILURE_CODES = [
  "invalid_session",
  "no_workspace_context",
  "tenant_inactive",
  "no_membership",
  "profile_suspended",
  "profile_removed",
  "profile_inactive",
  "write_failed",
  "refresh_failed",
  "sign_out_failed",
  "support_inactive",
] as const;

export type SessionContextFailureCode = (typeof SESSION_CONTEXT_FAILURE_CODES)[number];

/**
 * Production-safe copy for a signed-in Supabase auth session whose
 * authz.session_tenant_context row has expired (30-day cap, no sliding
 * renewal). Dev/staging error messages append remediation hints instead —
 * see lib/error-hints.ts. Never leak those hints to production users.
 */
export const SESSION_CONTEXT_EXPIRED_MESSAGE =
  "Your workspace session has expired. Please sign in again.";

/**
 * Sentinel prefix stamped into `Error.digest` by SessionContextError.
 *
 * Server actions cannot throw class instances across the wire: in production
 * Next.js masks the thrown message and forwards only `digest` (an error that
 * already carries a digest keeps it verbatim — see Next's
 * create-error-handler: "If the error already has a digest, respect the
 * original digest"). Prefix + failure code gives clients a stable, typed way
 * to recognize session-context failures from the reconstructed Error.
 */
export const SESSION_CONTEXT_ERROR_DIGEST_PREFIX = "SESSION_CONTEXT_ERROR:";

export function sessionContextErrorDigest(code: SessionContextFailureCode): string {
  return `${SESSION_CONTEXT_ERROR_DIGEST_PREFIX}${code}`;
}

/**
 * Recover the SessionContextFailureCode from any error-like value whose
 * `digest` carries the sentinel — either a live SessionContextError (same
 * runtime) or the masked Error reconstructed on the client after a server
 * action threw one. Returns null for everything else.
 */
export function sessionContextFailureCodeFromError(error: unknown): SessionContextFailureCode | null {
  if (typeof error !== "object" || error === null) return null;
  const digest = (error as { digest?: unknown }).digest;
  if (typeof digest !== "string" || !digest.startsWith(SESSION_CONTEXT_ERROR_DIGEST_PREFIX)) {
    return null;
  }
  const code = digest.slice(SESSION_CONTEXT_ERROR_DIGEST_PREFIX.length);
  return (SESSION_CONTEXT_FAILURE_CODES as readonly string[]).includes(code)
    ? (code as SessionContextFailureCode)
    : null;
}

/**
 * Resolve the public terminal auth code from either a server-action digest or
 * a structured API error code. Ordinary authorization 403s intentionally have
 * no mapped code and remain non-terminal.
 */
export function publicAuthErrorCodeFromError(error: unknown): PublicAuthErrorCode | null {
  if (typeof error !== "object" || error === null) return null;

  const sessionCode = sessionContextFailureCodeFromError(error);
  if (sessionCode) return SESSION_CONTEXT_TO_LOGIN_ERROR[sessionCode] ?? null;

  const apiCode = (error as { code?: unknown }).code;
  return typeof apiCode === "string" &&
    (PUBLIC_AUTH_ERROR_CODES as readonly string[]).includes(apiCode)
    ? (apiCode as PublicAuthErrorCode)
    : null;
}

export type EstablishSessionTenantContextError =
  | "invalid_session"
  | "unknown_workspace_domain"
  | "account_suspended"
  | "account_removed"
  | "no_access_for_workspace"
  | "session_refresh_failed"
  | "session_context_failed";

export const SESSION_CONTEXT_TO_ESTABLISH_ERROR: Partial<Record<SessionContextFailureCode, EstablishSessionTenantContextError>> = {
  profile_suspended: "account_suspended",
  profile_removed: "account_removed",
  no_membership: "no_access_for_workspace",
  profile_inactive: "no_access_for_workspace",
  tenant_inactive: "no_access_for_workspace",
  support_inactive: "no_access_for_workspace",
  refresh_failed: "session_refresh_failed",
};

export const SESSION_CONTEXT_TO_LOGIN_ERROR: Partial<Record<SessionContextFailureCode, PublicAuthErrorCode>> = {
  no_workspace_context: "no_workspace_context",
  profile_suspended: "account_suspended",
  profile_removed: "account_removed",
  no_membership: "no_access_for_workspace",
  profile_inactive: "no_access_for_workspace",
  tenant_inactive: "no_access_for_workspace",
  support_inactive: "no_access_for_workspace",
};

export const ESTABLISH_ERROR_TO_LOGIN_ERROR: Partial<Record<EstablishSessionTenantContextError, PublicAuthErrorCode>> = {
  invalid_session: "auth_callback_failed",
  unknown_workspace_domain: "unknown_workspace_domain",
  account_suspended: "account_suspended",
  account_removed: "account_removed",
  no_access_for_workspace: "no_access_for_workspace",
};

export const ESTABLISH_ERROR_STATUS: Partial<Record<EstablishSessionTenantContextError, number>> = {
  invalid_session: 401,
  unknown_workspace_domain: 403,
  account_suspended: 403,
  account_removed: 403,
  no_access_for_workspace: 403,
  session_refresh_failed: 500,
};
